/* ============================================
   NAOKO — PDF viewer (PDF.js) dla sekcji naoko-pdf-viewer
   File: assets/naoko-pdf-viewer.js  (ES module)

   Renderuje PDF (np. regulamin promocji) strona po stronie do <canvas>
   dopasowanego do szerokości kontenera + warstwa tekstowa PDF.js
   (zaznaczanie, ctrl+F). Strony rysowane leniwie (pozycja vs viewport przy
   scrollu), re-render po zmianie szerokości (ResizeObserver).

   Biblioteka: assets/naoko-pdfjs.min.js + naoko-pdfjs.worker.min.js
   (pdfjs-dist, legacy build; URL-e przekazane w data-atrybutach, bo asset
   nie ma dostępu do Liquid). Worker jest cross-origin (cdn.shopify.com) —
   PDF.js sam opakowuje go w blob z `import()`.

   Kontrakt CSS warstwy tekstowej (pdf_viewer.css): element strony musi mieć
   zmienną --total-scale-factor = viewport.scale; spany dostają --font-height
   i --scale-x od TextLayer.
   ============================================ */

const SEL = '.npv[data-pdf-url]';
const OBSERVE_MARGIN = 800; // px poza viewportem, od których zaczynamy render

function initViewer(root) {
  if (root.dataset.npvInit) return;
  root.dataset.npvInit = '1';

  const pdfUrl = root.dataset.pdfUrl;
  const libUrl = root.dataset.libUrl;
  const workerUrl = root.dataset.workerUrl;
  const qualityCap = parseFloat(root.dataset.quality || '2') || 2;
  const showNumbers = root.dataset.pageNumbers === 'true';
  const numberLabel = root.dataset.pageLabel || 'Strona [n] z [total]';

  const pagesEl = root.querySelector('.npv__pages');
  const statusEl = root.querySelector('.npv__status');
  const errorEl = root.querySelector('.npv__error');

  const setStatus = (txt) => {
    if (!statusEl) return;
    statusEl.textContent = txt || '';
    statusEl.hidden = !txt;
  };
  const fail = (err) => {
    console.error('[naoko-pdf-viewer]', err);
    setStatus('');
    root.classList.add('npv--error');
    if (errorEl) errorEl.hidden = false;
  };

  if (!pdfUrl || !libUrl || !pagesEl) return fail('brak konfiguracji');

  const pages = []; // { num, page, wrapper, canvas, textEl, rendered, rendering, visible, w, h }
  let pdfjs = null;
  let doc = null;
  let currentWidth = 0;

  const containerWidth = () => Math.max(1, Math.floor(pagesEl.clientWidth));

  const dpr = () => Math.min(qualityCap, window.devicePixelRatio || 1);

  async function renderPage(p) {
    if (p.rendering) {
      p.dirty = true;
      return;
    }
    p.rendering = true;
    p.rendered = false;
    try {
      do {
        p.dirty = false;
        const width = containerWidth();
        const base = p.page.getViewport({ scale: 1 });
        const scale = width / base.width;
        const viewport = p.page.getViewport({ scale });
        const ratio = dpr();

        // canvas w rozdzielczości urządzenia, CSS = 100% szerokości
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.setAttribute('role', 'presentation');
        const ctx = canvas.getContext('2d', { alpha: false });

        await p.page.render({
          canvas,
          canvasContext: ctx,
          viewport,
          transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
          background: '#ffffff',
        }).promise;

        // warstwa tekstowa
        const textEl = document.createElement('div');
        textEl.className = 'textLayer npv__text';
        p.wrapper.style.setProperty('--total-scale-factor', String(scale));
        p.wrapper.style.setProperty('--scale-factor', String(scale));
        try {
          const textLayer = new pdfjs.TextLayer({
            textContentSource: p.page.streamTextContent({ includeMarkedContent: true, disableNormalization: true }),
            container: textEl,
            viewport,
          });
          await textLayer.render();
          const eoc = document.createElement('div');
          eoc.className = 'endOfContent';
          textEl.append(eoc);
        } catch (e) {
          console.warn('[naoko-pdf-viewer] warstwa tekstowa', e);
        }

        // podmiana atomowa (bez mignięcia)
        p.wrapper.querySelectorAll('canvas, .npv__text').forEach((n) => n.remove());
        p.wrapper.prepend(canvas, textEl);
        p.canvas = canvas;
        p.textEl = textEl;
        p.wrapper.classList.add('npv__page--ready');
      } while (p.dirty);
      p.rendered = true;
    } catch (err) {
      console.error('[naoko-pdf-viewer] strona', p.num, err);
    } finally {
      p.rendering = false;
    }
  }

  // Leniwy render po pozycji względem viewportu (scroll/resize, throttling rAF).
  // Świadomie bez IntersectionObserver: w podglądach/iframe'ach (edytor motywu,
  // panel podglądu) callbacki IO bywają nieodpalane, a koszt sprawdzenia
  // kilkudziesięciu rectów jest pomijalny.
  let checkQueued = false;
  function checkVisible() {
    checkQueued = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    for (const p of pages) {
      const r = p.wrapper.getBoundingClientRect();
      p.visible = r.bottom > -OBSERVE_MARGIN && r.top < vh + OBSERVE_MARGIN;
      if (p.visible && !p.rendered && !p.rendering) renderPage(p);
    }
  }
  function queueCheck() {
    if (checkQueued) return;
    checkQueued = true;
    requestAnimationFrame(checkVisible);
  }

  let resizeTimer = 0;
  const ro = new ResizeObserver(() => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const w = containerWidth();
      if (Math.abs(w - currentWidth) < 2) return;
      currentWidth = w;
      for (const p of pages) p.rendered = false;
      checkVisible();
    }, 150);
  });

  (async () => {
    try {
      setStatus(root.dataset.loadingLabel || 'Ładowanie dokumentu…');
      pdfjs = await import(libUrl);
      if (workerUrl) pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

      const task = pdfjs.getDocument({
        url: pdfUrl,
        // fonty standardowe / cmapy nie są potrzebne dla typowych regulaminów
        // (osadzone fonty); bez nich brak dodatkowych requestów.
        isEvalSupported: false,
      });
      doc = await task.promise;
      const total = doc.numPages;
      root.dataset.pages = String(total);
      currentWidth = containerWidth();

      // szkielety stron z proporcją → brak skoków layoutu przy leniwym renderze
      const frag = document.createDocumentFragment();
      for (let n = 1; n <= total; n++) {
        const page = await doc.getPage(n);
        const vp = page.getViewport({ scale: 1 });
        const wrapper = document.createElement('div');
        wrapper.className = 'npv__page';
        wrapper.dataset.page = String(n);
        wrapper.style.aspectRatio = `${vp.width} / ${vp.height}`;
        wrapper.setAttribute('aria-label', numberLabel.replace('[n]', n).replace('[total]', total));
        frag.append(wrapper);
        if (showNumbers) {
          const cap = document.createElement('p');
          cap.className = 'npv__pagenum';
          cap.textContent = numberLabel.replace('[n]', n).replace('[total]', total);
          frag.append(cap);
        }
        pages.push({ num: n, page, wrapper, rendered: false, rendering: false, visible: false, dirty: false });
      }
      pagesEl.replaceChildren(frag);
      root.__npv = { pages, checkVisible, renderPage }; // debug/QA
      setStatus('');
      root.classList.add('npv--ready');

      window.addEventListener('scroll', queueCheck, { passive: true });
      window.addEventListener('resize', queueCheck, { passive: true });
      ro.observe(pagesEl);
      checkVisible();
    } catch (err) {
      fail(err);
    }
  })();
}

function initAll() {
  document.querySelectorAll(SEL).forEach(initViewer);
}

initAll();
// Theme Editor: sekcja przeładowana → nowy DOM
document.addEventListener('shopify:section:load', initAll);
